// @ts-nocheck
import { getShipTos } from "@insite/client-framework/Services/CustomersService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getBillTosDataView,
    getCurrentBillToState,
} from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTos from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTos";
import loadCurrentShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTos";
import { getCurrentShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import constants from "@store/constants";
import { useUserNotificationPreferencesContext } from "@store/Framework/Components/UserNotificationPreferencesContext/UserNotificationPreferencesContext";
import asString from "@store/Framework/Common/Utilities/asString";
import validateRequiredDropdown from "@store/Framework/Common/Utilities/validateRequiredDropdown";
import setFieldValue from "@store/Framework/Store/Greif/Components/InviteYourColleagueForm/Handlers/SetFieldValue";
import { InviteYourColleagueFormContext } from "@store/Library/Widgets/InviteYourColleague/InviteYourColleagueForm";
import useQueryString from "@toolkit/Framework/Common/Hooks/useQueryString";
import React, { ChangeEvent, FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    isRequired = "isRequired",
    fieldLabel = "fieldLabel",
    shipTosToDisplay = "shipTosToDisplay",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.isRequired]: boolean;
        [fields.fieldLabel]: string;
        [fields.shipTosToDisplay]: string;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    shipTo: asString(state.greif.components.inviteYourColleagueForm.fieldValues["shipTo"]),
    soldTo: asString(state.greif.components.inviteYourColleagueForm.fieldValues["soldTo"]),
    currentShipTosDataView: getCurrentShipTosDataView(state),
    currentBillToState: getCurrentBillToState(state),
    session: getSession(state),
    billToDataview: getBillTosDataView(state, {
        page: 1,
        pageSize: Number(props.fields.shipTosToDisplay),
    }),
});

const mapDispatchToProps = {
    loadCurrentShipTos,
    loadBillTos,
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ShipToStyles {
    shipToTextField?: TextFieldPresentationProps;
    shipToSelect?: SelectPresentationProps;
}

export const shipToStyles: ShipToStyles = {};

const styles = shipToStyles;

const ShipTo: FC<Props> = ({
    fields,
    shipTo,
    currentShipTosDataView,
    currentBillToState,
    billToDataview,
    soldTo,
    session,
    loadCurrentShipTos,
    setFieldValue,
}) => {
    const { isRequired, fieldLabel, shipTosToDisplay } = fields;
    const inviteYourColleagueFormContext = useContext(InviteYourColleagueFormContext);
    const [shipToErrorMessage, setShipToErrorMessage] = useState("");
    const [hasValidated, setHasValidated] = useState(false);
    const [shipTosOptions, setShipTosOptions] = useState<any[]>([]);
    const { customerSoldTo } = useUserNotificationPreferencesContext();

    const { h } = useQueryString();

    const currentShipTo = currentShipTosDataView && currentShipTosDataView.value;

    const findSelectedBillto = billToDataview?.value?.find(x => x.customerNumber === soldTo);
    const associatedShipTo = customerSoldTo?.soldTos?.find(x => x.customerNumber === soldTo);

    useEffect(() => {
        if (h && customerSoldTo) {
            if (associatedShipTo?.shipTos.length && soldTo) {
                setShipTosOptions(associatedShipTo?.shipTos);

                if (associatedShipTo?.shipTos?.length === 1) {
                    setFieldValue({ key: "shipTo", value: associatedShipTo.shipTos[0]?.label });
                } else {
                    setFieldValue({ key: "shipTo", value: "" });
                }
            }
        }
    }, [customerSoldTo, soldTo]);

    useEffect(() => {
        if (!h) {
            getShipTos({
                page: 1,
                pageSize: Number(shipTosToDisplay),
                billToId: findSelectedBillto?.id,
                expand: ["validation"],
                exclude: ["showAll"],
            })
                .then(e => {
                    setShipTosOptions(e.shipTos || []);
                    const currentShipto = e.shipTos?.find(x => x.id === session.shipToId);

                    if (e.shipTos?.length === 1) {
                        setFieldValue({ key: "shipTo", value: e.shipTos[0].label });
                        return;
                    }
                    setFieldValue({ key: "shipTo", value: "" });
                })
                .catch(f => {
                    if (f) {
                        setShipToErrorMessage("Ship to failed to load");
                    }
                });
        }
    }, [currentShipTosDataView, findSelectedBillto]);

    useEffect(() => {
        if (typeof shipTo === "undefined") {
            if (h) {
                setShipTosOptions([]);
                setFieldValue({ key: "shipTo", value: "" });
            }
        }
    }, [shipTo, currentShipTosDataView]);

    useEffect(() => {
        inviteYourColleagueFormContext.validators.shipTo = validateShipTo;
    }, [shipTo]);

    useEffect(() => {
        if (hasValidated) {
            validateShipTo();
        }
    }, [shipTo, hasValidated]);

    useEffect(() => {
        inviteYourColleagueFormContext.validators.shipTo = validateShipTo;
    }, [isRequired]);

    const validateShipTo = (value: string = shipTo || "") => {
        setHasValidated(true);
        let errorMessage: string | undefined;
        if (isRequired) {
            errorMessage = validateRequiredDropdown(value || "", fieldLabel);
        }
        setShipToErrorMessage(errorMessage || "");
        return !errorMessage;
    };

    const shipToChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        setFieldValue({ key: "shipTo", value: event.target.value });
        if (hasValidated) {
            validateShipTo(event.target.value);
        }
    };

    return (
        <>
            {shipTosOptions?.length === 1 || !soldTo ? (
                <TextField
                    label={fieldLabel}
                    value={shipTosOptions[0]?.label || ""}
                    disabled
                    placeholder={translate("Select a Ship-To")}
                    error={shipToErrorMessage}
                    {...styles.shipToTextField}
                    data-test-selector="ship-to-input-text"
                />
            ) : (
                <Select
                    label={fieldLabel}
                    value={shipTo || ""}
                    onChange={shipToChangeHandler}
                    required={isRequired}
                    disabled={!shipTosOptions.length}
                    error={shipToErrorMessage}
                    data-test-selector="ship-to-input-select"
                    {...styles.shipToSelect}
                >
                    <option value="">{translate("Select a Ship-To (select Sold-To first)")}</option>
                    {shipTosOptions.map((shipTo, id) => {
                        return shipTo.id === session.shipToId && !h ? (
                            <option key={id} value={shipTo.label}>
                                {`[${translate("CURRENT")}] ${shipTo.label}`}
                            </option>
                        ) : (
                            <option key={id} value={shipTo.label}>
                                {shipTo.label}
                            </option>
                        );
                    })}
                </Select>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ShipTo),
    definition: {
        displayName: "Ship-To",
        group: constants.widgetGroup.inviteYourColleague,
        icon: "input-pipe",
        allowedContexts: [constants.pageType.inviteYourColleague, constants.pageType.notificationPreferences],
        fieldDefinitions: [
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.fieldLabel,
                displayName: "Field Label",
                editorTemplate: "TextField",
                defaultValue: "Customer Ship-To",
                fieldType: "Translatable",
            },
            {
                name: fields.shipTosToDisplay,
                displayName: "ShipTos to display",
                editorTemplate: "IntegerField",
                max: 250,
                defaultValue: 250,
                tooltip: "Platform default: 20",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
